/**
 * The client identity we report to the server on every request via the
 * `x-client-version` header, so System Admin can show which build each
 * user is on. Resolves once at startup:
 *   - desktop shell → the Tauri app version (e.g. "0.1.23")
 *   - plain browser → "web"
 * The server stamps it onto `dashboard_users.client_version` in the
 * `requireAuth` hook (alongside last-seen + name). It updates every
 * request, so after a user installs an update the new version is recorded
 * the first time the fresh build calls the API.
 */
import { isTauri } from "./notifications/desktopBridge.js";

let cached = "web";

/** Synchronous accessor for the request header. Defaults to "web" until
 * `initClientVersion()` resolves the desktop version (a few ms after launch). */
export function getClientVersion(): string {
  return cached;
}

/** Resolve the client version once at app startup. Safe to call repeatedly. */
export async function initClientVersion(): Promise<void> {
  if (!isTauri()) {
    cached = "web";
    return;
  }
  try {
    const { getVersion } = await import("@tauri-apps/api/app");
    const v = (await getVersion()).trim();
    if (v) cached = v;
    else cached = "desktop";
  } catch {
    // Older shell without the version API — still clearly a desktop client.
    cached = "desktop";
  }
}

/** Test-only reset. */
export function _resetClientVersionForTests(): void {
  cached = "web";
}
